// src/components/HowInstashipWorks.tsx
import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import howItWorksPng from "../assets/how-it-works.png";
import howItWorksPhonePng from "../assets/how-it-works-phone.png";

const PRIMARY = "#3498db";
const NAVY = "#0F172A";

const PulseDot = ({ color }: { color: string }) => (
  <motion.div
    style={{ width: 9, height: 9, borderRadius: "50%", background: color, flexShrink: 0 }}
    animate={{ scale: [1, 1.4, 1], opacity: [1, 0.55, 1] }}
    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
  />
);

const HowInstashipWorks: React.FC = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 940);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth >= 940 && window.innerWidth < 1400);
  const [imgLoaded, setImgLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  useEffect(() => {
    const h = () => {
      const w = window.innerWidth;
      setIsMobile(w < 940);
      setIsNarrow(w >= 940 && w < 1400);
    };
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  return (
    <section
      ref={ref}
      style={{
        background: "#ffffff",
        padding: isMobile ? "60px 0 48px" : "90px 0 72px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* BG glows */}
      <div style={{ position: "absolute", top: -100, right: -100, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(52,152,219,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.07) 0%, transparent 70%)", pointerEvents: "none" }} />

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: "center", marginBottom: isMobile ? 36 : 52 }}
        >
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 10,
            background: "rgba(52,152,219,0.12)", border: "1.5px solid rgba(52,152,219,0.35)",
            borderRadius: 50, padding: "10px 26px", marginBottom: 22,
            boxShadow: "0 4px 20px rgba(52,152,219,0.18)",
          }}>
            <PulseDot color={PRIMARY} />
            <span style={{ fontSize: 15, fontWeight: 800, color: PRIMARY, letterSpacing: "0.08em", textTransform: "uppercase" }}>How It Works</span>
          </div>

          <h2 style={{
            fontSize: isMobile ? "1.9rem" : "2.8rem",
            fontWeight: 900,
            color: NAVY,
            lineHeight: 1.12,
            letterSpacing: "-0.025em",
            margin: "0 0 14px",
          }}>
            From Your Factory Floor{" "}
            <span style={{
              background: `linear-gradient(90deg, ${PRIMARY}, #60c0ff)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}>
              to Customer&apos;s Door
            </span>
          </h2>

          <p style={{
            fontSize: isMobile ? "1rem" : "1.1rem",
            color: "#64748b",
            whiteSpace: "normal",
            margin: "0 auto",
            lineHeight: 1.7,
          }}>
            Hand us your goods — we handle everything from pickup to delivery.
          </p>
        </motion.div>

        {/* Diagram image */}
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <img
            src={isMobile ? howItWorksPhonePng : howItWorksPng}
            alt="How Instaship Works — logistics flow diagram"
            fetchPriority="high"
            onLoad={() => setImgLoaded(true)}
            style={{
              width: (isMobile || isNarrow) ? "100%" : "130.625%",
              height: "auto",
              display: "block",
              marginLeft: (isMobile || isNarrow) ? "0" : "-15.3125%",
              opacity: imgLoaded ? 1 : 0,
              transition: "opacity 0.6s ease",
            }}
          />
        </motion.div>

      </div>
    </section>
  );
};

export default HowInstashipWorks;
